import type {
  RealtimeBusinessEventType,
  RealtimeEvent,
  RealtimeStreamCloseReason,
} from "../types/realtime";

/** Server bound on one serialized frame (plutus-backend RealtimeStreamService MAX_EVENT_BYTES). */
export const REALTIME_MAX_FRAME_BYTES = 8 * 1024;

const CLOSE_REASONS: readonly RealtimeStreamCloseReason[] = [
  "authentication-expired",
  "lifetime-reached",
  "transport-unavailable",
  "server-shutdown",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isPositiveSafeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0;

type PayloadValidator = (payload: unknown) => boolean;

/**
 * One payload validator per event type the website understands. Add an entry
 * here together with the union member in `types/realtime.ts`. Types missing
 * from this map are dropped by `parseRealtimeEvent`, which lets the backend
 * ship a new event before the website subscribes to it.
 */
export const REALTIME_EVENT_VALIDATORS: Record<RealtimeEvent["type"], PayloadValidator> = {
  "stream.ready": (payload) =>
    isRecord(payload)
    && typeof payload.heartbeatIntervalMs === "number"
    && Number.isFinite(payload.heartbeatIntervalMs)
    && payload.heartbeatIntervalMs > 0
    && typeof payload.closesAtUtc === "string",
  "stream.heartbeat": (payload) => isRecord(payload),
  "stream.closing": (payload) =>
    isRecord(payload) && CLOSE_REASONS.includes(payload.reason as RealtimeStreamCloseReason),
  "notification.inbox-changed": (payload) =>
    isRecord(payload) && isPositiveSafeInteger(payload.notificationMessageId),
};

export const REALTIME_BUSINESS_EVENT_TYPES: readonly RealtimeBusinessEventType[] = [
  "notification.inbox-changed",
];

/**
 * Turns one SSE frame (`event` name + `data` text) into a typed event, or null
 * when the frame is unknown or malformed. Never throws.
 */
export function parseRealtimeEvent(eventName: string, data: string): RealtimeEvent | null {
  const validate = (REALTIME_EVENT_VALIDATORS as Record<string, PayloadValidator | undefined>)[eventName];
  if (!validate) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  if (parsed.type !== eventName || parsed.version !== 1) return null;
  if (!validate(parsed.payload)) return null;
  return { type: eventName, version: 1, payload: parsed.payload } as RealtimeEvent;
}
